import React from 'react';
import { Link } from 'react-router-dom';
import './LegalPage.css';

const EFFECTIVE_DATE = 'May 9, 2026';

const PrivacyPage: React.FC = () => (
  <div className="legal-page">
    <div className="legal-header">
      <h1>Privacy Policy</h1>
      <p className="legal-meta">Effective date: {EFFECTIVE_DATE}</p>
    </div>

    <div className="legal-toc">
      <h2>Contents</h2>
      <ol>
        <li><a href="#information">Information We Collect</a></li>
        <li><a href="#usage">How We Use Your Information</a></li>
        <li><a href="#sharing-data">Data Sharing</a></li>
        <li><a href="#retention-data">Data Retention</a></li>
        <li><a href="#security">Security</a></li>
        <li><a href="#cookies">Cookies</a></li>
        <li><a href="#rights">Your Rights</a></li>
        <li><a href="#children">Children's Privacy</a></li>
        <li><a href="#changes-privacy">Changes to This Policy</a></li>
        <li><a href="#contact-privacy">Contact</a></li>
      </ol>
    </div>

    <div className="legal-section" id="information">
      <h2>1. Information We Collect</h2>
      <p>We collect information in the following ways:</p>

      <p><strong>Information you provide directly:</strong></p>
      <ul>
        <li><strong>Account registration:</strong> name, email address, and password (stored as a
          bcrypt hash — we never store your plaintext password)</li>
        <li><strong>OAuth sign-in:</strong> basic profile information (name, email, profile picture)
          from the provider you choose (Google, LinkedIn, Facebook, or X/Twitter)</li>
        <li><strong>Documents:</strong> the files you upload and any metadata associated with
          them (file name, size, page count)</li>
        <li><strong>Contact form submissions:</strong> your email address and the message you send</li>
      </ul>

      <p><strong>Information collected automatically:</strong></p>
      <ul>
        <li><strong>Usage data:</strong> pages viewed, documents opened, downloads, and print
          events, associated with a timestamp and an anonymised IP address</li>
        <li><strong>Log data:</strong> server logs including request paths, HTTP status codes,
          and error information, retained for security and debugging</li>
      </ul>

      <p>
        We do <strong>not</strong> read the content of your documents. Files are stored encrypted
        at rest in Amazon S3.
      </p>
    </div>

    <div className="legal-section" id="usage">
      <h2>2. How We Use Your Information</h2>
      <p>We use the information we collect to:</p>
      <ul>
        <li>Create and manage your account and authenticate you</li>
        <li>Provide, operate, and improve the Service</li>
        <li>Send transactional emails (email verification, password reset, notifications)</li>
        <li>Respond to contact form enquiries and support requests</li>
        <li>Generate document analytics visible to document owners (page views, download counts)</li>
        <li>Detect and prevent fraud, abuse, and security incidents</li>
        <li>Comply with legal obligations</li>
      </ul>
      <p>
        We do not use your data for advertising purposes or sell it to third parties.
      </p>
    </div>

    <div className="legal-section" id="sharing-data">
      <h2>3. Data Sharing</h2>
      <p>We do not sell, rent, or trade your personal information. We may share it only in the
        following limited circumstances:</p>
      <ul>
        <li>
          <strong>Service providers:</strong> We use Amazon Web Services (AWS) to host the Service,
          store documents, and send transactional email via Amazon SES. AWS processes data on our
          behalf under data-processing agreements.
        </li>
        <li>
          <strong>Legal requirements:</strong> We may disclose information if required to do so by
          law, regulation, or a valid legal process (e.g., a court order or subpoena).
        </li>
        <li>
          <strong>Protection of rights:</strong> We may share information to protect the rights,
          property, or safety of KeeDocs, our users, or the public.
        </li>
        <li>
          <strong>Business transfer:</strong> In the event of a merger, acquisition, or sale of
          assets, your information may be transferred as part of that transaction. We will notify
          you before your data is transferred and becomes subject to a different privacy policy.
        </li>
      </ul>
    </div>

    <div className="legal-section" id="retention-data">
      <h2>4. Data Retention</h2>
      <ul>
        <li><strong>Anonymous uploads:</strong> documents and associated metadata are automatically
          deleted after <strong>7 days</strong></li>
        <li><strong>Account documents:</strong> retained while your account is active and deleted
          within 30 days of account closure</li>
        <li><strong>Activity logs:</strong> retained for up to 90 days</li>
        <li><strong>Email logs:</strong> retained for up to 30 days for delivery troubleshooting</li>
      </ul>
      <p>
        You can delete individual documents at any time from your dashboard. To close your account
        and request deletion of all associated data, contact us via the{' '}
        <Link to="/contact">Contact page</Link>.
      </p>
    </div>

    <div className="legal-section" id="security">
      <h2>5. Security</h2>
      <p>
        We take security seriously and implement appropriate technical and organisational measures
        to protect your personal information:
      </p>
      <ul>
        <li>Documents are stored encrypted at rest using AES-256 (AWS S3 SSE)</li>
        <li>All data in transit is encrypted using TLS 1.2 or higher</li>
        <li>Passwords are hashed using bcrypt with a cost factor appropriate for production</li>
        <li>Access tokens expire after 1 hour; refresh tokens after 30 days</li>
        <li>DynamoDB tables are encrypted at rest with server-side encryption</li>
      </ul>
      <p>
        No method of transmission or storage is 100% secure. If you believe your account has been
        compromised, please contact us immediately.
      </p>
    </div>

    <div className="legal-section" id="cookies">
      <h2>6. Cookies</h2>
      <p>
        KeeDocs uses only the cookies necessary to operate the Service:
      </p>
      <ul>
        <li><strong>Authentication tokens:</strong> stored in browser localStorage to keep you
          signed in between sessions</li>
      </ul>
      <p>
        We do not use third-party advertising cookies or tracking pixels.
      </p>
    </div>

    <div className="legal-section" id="rights">
      <h2>7. Your Rights</h2>
      <p>
        Depending on your location, you may have the following rights regarding your personal data:
      </p>
      <ul>
        <li><strong>Access:</strong> request a copy of the personal data we hold about you</li>
        <li><strong>Correction:</strong> ask us to correct inaccurate or incomplete data</li>
        <li><strong>Deletion:</strong> request deletion of your personal data (subject to legal
          retention obligations)</li>
        <li><strong>Portability:</strong> receive your data in a machine-readable format</li>
        <li><strong>Objection:</strong> object to processing of your data in certain circumstances</li>
        <li><strong>Restriction:</strong> request that we restrict processing of your data while
          a complaint is being investigated</li>
      </ul>
      <p>
        To exercise any of these rights, please use our <Link to="/contact">Contact page</Link> or
        email <a href="mailto:privacy@keedocs.com">privacy@keedocs.com</a>. We will respond within
        30 days.
      </p>
    </div>

    <div className="legal-section" id="children">
      <h2>8. Children's Privacy</h2>
      <p>
        The Service is not directed to children under the age of 18. We do not knowingly collect
        personal information from children. If you believe a child has provided us with personal
        information, please contact us and we will delete it promptly.
      </p>
    </div>

    <div className="legal-section" id="changes-privacy">
      <h2>9. Changes to This Policy</h2>
      <p>
        <strong>This Privacy Policy is subject to change at any time without prior notice.</strong>{' '}
        We will update the effective date above when changes are made. It is your responsibility
        to review this policy periodically. Continued use of the Service after changes are posted
        constitutes your acceptance of the revised policy.
      </p>
    </div>

    <div className="legal-section" id="contact-privacy">
      <h2>10. Contact</h2>
      <div className="legal-contact">
        <p>
          For privacy-related enquiries, data access requests, or to report a concern, please use
          our <Link to="/contact">Contact page</Link> or email{' '}
          <a href="mailto:privacy@keedocs.com">privacy@keedocs.com</a>.
        </p>
        <p style={{ marginBottom: 0 }}>
          For legal notices, please review our{' '}
          <Link to="/terms">Terms of Use</Link>.
        </p>
      </div>
    </div>
  </div>
);

export default PrivacyPage;
